import type { ReactNode } from "react";

// The shared cloud-frontend masthead: logo + wordmark + subtitle, with an
// optional right slot. Every public/landing-chrome surface (Landing, the
// agreement, the legal pages, /apply, the desktop-required gate, the Done
// screen) used to inline an identical copy of this block; they drifted over
// time. One component, one source of truth.
//
// - `right` overrides the right slot (e.g. the legal pages' "Back to Aurora"
//   link). Defaults to the "early access" status pill.
// - `nav` adds link(s) just left of the status pill (e.g. the Landing
//   header's FAQ entry). Ignored when `right` overrides the slot.
// - `brandHref` wraps the brand in a home link (e.g. /apply, where the logo
//   is a way back to the marketing site — a REAL navigation, since /apply is
//   outside the gated SPA). Defaults to a plain div.
// - `onBrandClick` wraps the brand in a button instead (e.g. Welcome's
//   escape back to Landing, 2026-07-04) — an in-app state swap, not a page
//   navigation, so it must NOT be a real `href` (a real nav would reload
//   Shell and re-run its mount-time restore logic, bouncing the user right
//   back to the gate instead of escaping it). Takes precedence over
//   `brandHref` if both are somehow passed.
// - `hideStatus` drops the "early access" pill without touching `nav` —
//   Landing passes this once self-serve checkout is open (front-door spec,
//   2026-07-04); every other call site leaves it unset and is unaffected.

const BrandInner = () => (
  <>
    <img className="landing-logo" src="/aurora-icon.png" alt="Aurora logo" />
    <div className="landing-wordmark">
      Aurora
      <small>Pattern analysis for coercive control</small>
    </div>
  </>
);

const DefaultStatus = () => (
  <div className="landing-status">
    <span className="landing-status-dot" />
    Early access
  </div>
);

export function CloudMast({
  right,
  brandHref,
  onBrandClick,
  nav,
  hideStatus,
}: {
  right?: ReactNode;
  brandHref?: string;
  onBrandClick?: () => void;
  nav?: ReactNode;
  hideStatus?: boolean;
}) {
  return (
    <header className="landing-mast">
      {onBrandClick ? (
        <button
          type="button"
          className="landing-brand landing-brand-button"
          onClick={onBrandClick}
          aria-label="Aurora — back to start"
        >
          <BrandInner />
        </button>
      ) : brandHref ? (
        <a className="landing-brand" href={brandHref} aria-label="Aurora — home">
          <BrandInner />
        </a>
      ) : (
        <div className="landing-brand">
          <BrandInner />
        </div>
      )}
      {right ?? (
        <div className="landing-mast-end">
          {nav}
          {hideStatus ? null : <DefaultStatus />}
        </div>
      )}
    </header>
  );
}
